import api from '../api/axios';

export interface Notification {
  id: number;
  type: 'info' | 'warning' | 'success' | 'error';
  title: string;
  message: string;
  read: boolean;
  readAt?: string;
  link?: string;
  createdAt: string;
}

export interface NotificationStreamPayload {
  count: number;
  notification?: Pick<Notification, 'id' | 'type' | 'title' | 'message' | 'link' | 'createdAt'> | null;
}

export const notificationService = {
  async getAll() {
    const response = await api.get('/notifications');
    return response.data;
  },

  async markAsRead(id: number) {
    const response = await api.put(`/notifications/${id}/read`);
    return response.data;
  },

  async markAllAsRead() {
    const response = await api.put('/notifications/mark-all-read');
    return response.data;
  },

  async getUnreadCount() {
    const response = await api.get('/notifications/unread-count');
    return response.data;
  },

  /** Subscribe to real-time unread count via SSE (fetch + Authorization header) */
  subscribeUnreadCount(
    onUpdate: (payload: NotificationStreamPayload) => void,
    onDisconnect?: () => void
  ): () => void {
    const API_URL = (process.env.REACT_APP_API_URL || 'https://darb.aiastra.io').replace(/\/$/, '');
    const token = localStorage.getItem('token');
    let active = true;
    let controller: AbortController | null = null;
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

    const isAbortError = (err: unknown): boolean => {
      if (!err || typeof err !== 'object') return false;
      const e = err as { name?: string; message?: string; code?: number };
      return (
        e.name === 'AbortError' ||
        e.code === 20 ||
        /abort/i.test(String(e.message || ''))
      );
    };

    const connect = async () => {
      if (!active) return;

      controller = new AbortController();

      try {
        const response = await fetch(`${API_URL}/api/notifications/unread-stream`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          signal: controller.signal,
        });
        if (!response.ok || !response.body) {
          throw new Error(`SSE failed: ${response.status}`);
        }

        reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (active) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split('\n');
          buffer = parts.pop() || '';
          for (const line of parts) {
            if (line.startsWith('data: ')) {
              try {
                const payload = JSON.parse(line.slice(6)) as NotificationStreamPayload;
                if (typeof payload.count === 'number') {
                  onUpdate(payload);
                }
              } catch {
                // ignore malformed events
              }
            }
          }
        }
      } catch (err) {
        if (!active || isAbortError(err)) return;
        onDisconnect?.();
      } finally {
        try {
          reader?.releaseLock();
        } catch {
          // reader may already be released
        }
        reader = null;
        controller = null;
      }
    };

    void connect().catch((err) => {
      if (!active || isAbortError(err)) return;
      onDisconnect?.();
    });

    return () => {
      active = false;
      void reader?.cancel().catch(() => {});
      controller?.abort();
      reader = null;
      controller = null;
    };
  },

  async delete(id: number) {
    const response = await api.delete(`/notifications/${id}`);
    return response.data;
  }
};

